import { Image as ImageIcon, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import type { FooterSeparator } from '@goenhub/shared';

import { ImageGalleryDialog } from '@/components/common/ImageGalleryDialog';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { useMailFooter, useUpdateMailFooter } from '@/hooks/useMailSettings';

interface FormValues {
  separator: FooterSeparator;
  logoUrl: string;
  webUrl: string;
  snsLinks: string;
}

const defaults: FormValues = {
  separator: 'line',
  logoUrl: '',
  webUrl: '',
  snsLinks: '',
};

export function FooterSection() {
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data, isLoading } = useMailFooter();
  const updateMutation = useUpdateMailFooter();
  const form = useForm<FormValues>({ defaultValues: defaults });
  const [logoGalleryOpen, setLogoGalleryOpen] = useState(false);
  const logoUrl = form.watch('logoUrl');

  useEffect(() => {
    if (data) form.reset({ ...defaults, ...data });
  }, [data, form]);

  const onSubmit = async (values: FormValues) => {
    try {
      await updateMutation.mutateAsync(values);
      toast.success('フッター設定を保存しました');
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mb-8">
      <h3 className="text-base font-bold mb-4 pb-2 border-b border-[var(--border)]">
        フッター設定
      </h3>
      <div>
        <Label>区切り線スタイル</Label>
        <Select
          value={form.watch('separator')}
          onValueChange={(v) => form.setValue('separator', v as FooterSeparator)}
          disabled={!canEdit}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="line">── line（標準）</SelectItem>
            <SelectItem value="double">══ double</SelectItem>
            <SelectItem value="dots">… dots</SelectItem>
            <SelectItem value="none">なし</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>ロゴ画像</Label>
        {logoUrl ? (
          <div className="space-y-2">
            <img
              src={logoUrl}
              alt="ロゴ"
              className="max-h-16 rounded border border-[var(--border)] bg-white p-2"
            />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setLogoGalleryOpen(true)}
                disabled={!canEdit}
              >
                <ImageIcon className="size-3.5 mr-1" />
                ロゴを変更
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => form.setValue('logoUrl', '', { shouldDirty: true })}
                disabled={!canEdit}
              >
                <Trash2 className="size-3.5 mr-1" />
                ロゴを削除
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => setLogoGalleryOpen(true)}
            disabled={!canEdit}
          >
            <ImageIcon className="size-3.5 mr-1" />
            ロゴをアップロード
          </Button>
        )}
        {/* register で値を保持（hidden input） */}
        <input type="hidden" {...form.register('logoUrl')} />
      </div>
      <div>
        <Label htmlFor="f-web">Web サイト URL</Label>
        <Input id="f-web" placeholder="https://caudesign.jp" {...form.register('webUrl')} disabled={!canEdit} />
      </div>
      <div>
        <Label htmlFor="f-sns">SNS リンク（自由記述、例: Facebook:https://...）</Label>
        <Textarea
          id="f-sns"
          rows={3}
          {...form.register('snsLinks')}
          disabled={!canEdit}
          placeholder="Facebook:https://www.facebook.com/caudesign"
        />
      </div>
      {canEdit && (
        <div className="flex justify-end">
          <Button type="submit" variant="primary" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </div>
      )}

      <ImageGalleryDialog
        open={logoGalleryOpen}
        onOpenChange={setLogoGalleryOpen}
        title="ロゴ画像を選択"
        onSelect={(url) =>
          form.setValue('logoUrl', url, { shouldDirty: true })
        }
      />
    </form>
  );
}
